import { motion, useIsPresent, type Variants } from "framer-motion";
import { type ReactNode, useCallback } from "react";

import type { TransitionDirection } from "./transitionDirection";

const SLIDE_DURATION = 0.25;
const FADE_DURATION = 0.15;

/**
 * Custom value passed through AnimatePresence (for exit) and motion.div (for
 * initial/animate). Using an object lets the exit variant access the current
 * skipAnimation flag even though the exiting component's props are stale.
 */
interface TransitionCustom {
  direction?: TransitionDirection;
  skipAnimation?: boolean;
}

function getDuration(custom: TransitionCustom | undefined): number {
  if (custom?.skipAnimation) return 0;
  return custom?.direction ? SLIDE_DURATION : FADE_DURATION;
}

const variants: Variants = {
  initial: (custom: TransitionCustom | undefined) => {
    const dir = custom?.direction;
    const transition = {
      duration: getDuration(custom),
      ease: "easeInOut" as const,
    };
    if (dir === "left") return { x: "100%", opacity: 1, transition };
    if (dir === "right") return { x: "-100%", opacity: 1, transition };
    return { opacity: 0, transition };
  },
  animate: (custom: TransitionCustom | undefined) => ({
    x: 0,
    opacity: 1,
    transition: { duration: getDuration(custom), ease: "easeInOut" as const },
  }),
  exit: (custom: TransitionCustom | undefined) => {
    const dir = custom?.direction;
    const transition = {
      duration: getDuration(custom),
      ease: "easeInOut" as const,
    };
    if (dir === "left") return { x: "-100%", opacity: 1, transition };
    if (dir === "right") return { x: "100%", opacity: 1, transition };
    return { opacity: 0, transition };
  },
};

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
  onEnterComplete?: () => void;
  direction?: TransitionDirection;
  /** When true, skip enter/exit animation (instant mount after drag-commit). */
  skipAnimation?: boolean;
}

export function PageTransition({
  children,
  className,
  onEnterComplete,
  direction = null,
  skipAnimation = false,
}: PageTransitionProps) {
  const isPresent = useIsPresent();

  const handleAnimationComplete = useCallback(() => {
    if (isPresent && onEnterComplete) {
      onEnterComplete();
    }
  }, [isPresent, onEnterComplete]);

  return (
    <motion.div
      custom={{ direction, skipAnimation }}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      onAnimationComplete={handleAnimationComplete}
      className={className}
    >
      {children}
    </motion.div>
  );
}
